import React from "react";
import PropTypes from "prop-types";
import { remove } from "remove-accents";
import pDebounce from "p-debounce";
import CacheMap from "../../lib/form/CacheMap";
import Select from "./Select";

const cache = new CacheMap("fetch_company_siret");

const fetchData = () => {
  return pDebounce(async (dependOf, dependOfValue, setItems) => {
    if (typeof dependOfValue !== "string") {
      setItems([]);
      return undefined;
    }

    const query = encodeURIComponent(remove(dependOfValue).toLowerCase());

    let result;
    if (cache.has(query)) {
      result = cache.get(query);
    } else {
      const response = await fetch(
        `https://sirene-as-api.timeonegroup.com/etablissements/?q=${query}`,
        {
          method: "GET",
          headers: {},
          mode: "cors",
          cache: "default",
        }
      );
      result = await response.json();
      cache.set(query, result);
    }

    setItems(
      result.map((etablissement) => ({
        value: etablissement.siret,
        label: `${etablissement.siret} - ${etablissement.geo_adresse}`,
        address:
          etablissement.geo_l4 +
          (etablissement.geo_l5 !== null ? `, ${etablissement.geo_l5}` : ""),
        postcode: etablissement.codepostaletablissement,
        codeNaf: etablissement.activiteprincipaleetablissement,
        inseeCode: etablissement.codecommuneetablissement,
      }))
    );
  }, 500);
};

function CompanySIRET({
  methods,
  label,
  name,
  addressName,
  postcodeName,
  codeNafName,
  cityInseeCodeName,
  dependOf,
  required,
  variant,
}) {
  const { setValue, register } = methods;

  return (
    <React.Fragment>
      <Select
        name={name}
        label={label}
        required={required}
        variant={variant}
        methods={methods}
        items={[]}
        dependOf={dependOf}
        dependOfLoader={fetchData()}
        onChange={(event, newValue) => {
          setValue(addressName, newValue?.address);
          setValue(postcodeName, newValue?.postcode);
          setValue(cityInseeCodeName, newValue?.inseeCode);
          setValue(codeNafName, newValue?.codeNaf);
        }}
      />
      <input {...register(addressName)} type="hidden" />
      <input {...register(postcodeName)} type="hidden" />
      <input {...register(cityInseeCodeName)} type="hidden" />
      <input {...register(codeNafName)} type="hidden" />
    </React.Fragment>
  );
}

CompanySIRET.defaultProps = {
  label: "Votre établissement SIRET",
  name: "company_siret",
  addressName: "address",
  postcodeName: "zipcode",
  codeNafName: "company_naf_code",
  cityInseeCodeName: "insee_code",
  dependOf: "siren",
  required: "Merci de saisir votre établissement SIRET",
  variant: "outlined",
};

CompanySIRET.propTypes = {
  /**
   * methods du useForm()
   */
  methods: PropTypes.instanceOf(Object),
  /**
   * Label du champ
   */
  label: PropTypes.string,
  /**
   * Name du tag HTML input
   */
  name: PropTypes.string,
  /**
   * Name du tag HTML input contenant l'adresse de l'établissement
   */
  addressName: PropTypes.string,
  /**
   * Name du tag HTML input contenant le code insee de la commune
   */
  cityInseeCodeName: PropTypes.string,
  /**
   * Name du tag HTML input contenant le code postale de l'établissement
   */
  postcodeName: PropTypes.string,
  /**
   * Name du tag HTML input contenant le code NAF de l'établissement
   */
  codeNafName: PropTypes.string,
  /**
   * Nom du champ de dépendance dans le formulaire
   */
  dependOf: PropTypes.string,
  /**
   * Message d'erreur si le champ est requis
   */
  required: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /**
   * The variant to use
   * 'filled'
   * | 'outlined'
   * | 'standard'
   */
  variant: PropTypes.string,
};

export default CompanySIRET;
